/**
 * Canonical hand metadata for the 169 poker hands in a standard grid layout
 */

import type { HandSubtype, HandType } from '@/types/chart';

export interface HandMetadata {
  hand: string;
  row: number;
  col: number;
  type: HandType;
  subtype: HandSubtype;
}

/**
 * Map of hand name to metadata (position and type)
 */
export const HAND_METADATA: Record<string, HandMetadata> = {
  // Row 0 - Aces
  AA: { hand: 'AA', row: 0, col: 0, type: 'pair', subtype: 'premium-pair' },
  AKs: { hand: 'AKs', row: 0, col: 1, type: 'suited', subtype: 'broadway' },
  AQs: { hand: 'AQs', row: 0, col: 2, type: 'suited', subtype: 'broadway' },
  AJs: { hand: 'AJs', row: 0, col: 3, type: 'suited', subtype: 'broadway' },
  ATs: { hand: 'ATs', row: 0, col: 4, type: 'suited', subtype: 'broadway' },
  A9s: { hand: 'A9s', row: 0, col: 5, type: 'suited', subtype: 'suited-ace' },
  A8s: { hand: 'A8s', row: 0, col: 6, type: 'suited', subtype: 'suited-ace' },
  A7s: { hand: 'A7s', row: 0, col: 7, type: 'suited', subtype: 'suited-ace' },
  A6s: { hand: 'A6s', row: 0, col: 8, type: 'suited', subtype: 'suited-ace' },
  A5s: { hand: 'A5s', row: 0, col: 9, type: 'suited', subtype: 'suited-ace' },
  A4s: { hand: 'A4s', row: 0, col: 10, type: 'suited', subtype: 'suited-ace' },
  A3s: { hand: 'A3s', row: 0, col: 11, type: 'suited', subtype: 'suited-ace' },
  A2s: { hand: 'A2s', row: 0, col: 12, type: 'suited', subtype: 'suited-ace' },

  // Row 1 - Kings
  AKo: { hand: 'AKo', row: 1, col: 0, type: 'offsuit', subtype: 'broadway' },
  KK: { hand: 'KK', row: 1, col: 1, type: 'pair', subtype: 'premium-pair' },
  KQs: { hand: 'KQs', row: 1, col: 2, type: 'suited', subtype: 'broadway' },
  KJs: { hand: 'KJs', row: 1, col: 3, type: 'suited', subtype: 'broadway' },
  KTs: { hand: 'KTs', row: 1, col: 4, type: 'suited', subtype: 'broadway' },
  K9s: { hand: 'K9s', row: 1, col: 5, type: 'suited', subtype: 'suited-other' },
  K8s: { hand: 'K8s', row: 1, col: 6, type: 'suited', subtype: 'suited-other' },
  K7s: { hand: 'K7s', row: 1, col: 7, type: 'suited', subtype: 'suited-other' },
  K6s: { hand: 'K6s', row: 1, col: 8, type: 'suited', subtype: 'suited-other' },
  K5s: { hand: 'K5s', row: 1, col: 9, type: 'suited', subtype: 'suited-other' },
  K4s: { hand: 'K4s', row: 1, col: 10, type: 'suited', subtype: 'suited-other' },
  K3s: { hand: 'K3s', row: 1, col: 11, type: 'suited', subtype: 'suited-other' },
  K2s: { hand: 'K2s', row: 1, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 2 - Queens
  AQo: { hand: 'AQo', row: 2, col: 0, type: 'offsuit', subtype: 'broadway' },
  KQo: { hand: 'KQo', row: 2, col: 1, type: 'offsuit', subtype: 'broadway' },
  QQ: { hand: 'QQ', row: 2, col: 2, type: 'pair', subtype: 'premium-pair' },
  QJs: { hand: 'QJs', row: 2, col: 3, type: 'suited', subtype: 'broadway' },
  QTs: { hand: 'QTs', row: 2, col: 4, type: 'suited', subtype: 'broadway' },
  Q9s: { hand: 'Q9s', row: 2, col: 5, type: 'suited', subtype: 'suited-other' },
  Q8s: { hand: 'Q8s', row: 2, col: 6, type: 'suited', subtype: 'suited-other' },
  Q7s: { hand: 'Q7s', row: 2, col: 7, type: 'suited', subtype: 'suited-other' },
  Q6s: { hand: 'Q6s', row: 2, col: 8, type: 'suited', subtype: 'suited-other' },
  Q5s: { hand: 'Q5s', row: 2, col: 9, type: 'suited', subtype: 'suited-other' },
  Q4s: { hand: 'Q4s', row: 2, col: 10, type: 'suited', subtype: 'suited-other' },
  Q3s: { hand: 'Q3s', row: 2, col: 11, type: 'suited', subtype: 'suited-other' },
  Q2s: { hand: 'Q2s', row: 2, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 3 - Jacks
  AJo: { hand: 'AJo', row: 3, col: 0, type: 'offsuit', subtype: 'broadway' },
  KJo: { hand: 'KJo', row: 3, col: 1, type: 'offsuit', subtype: 'broadway' },
  QJo: { hand: 'QJo', row: 3, col: 2, type: 'offsuit', subtype: 'broadway' },
  JJ: { hand: 'JJ', row: 3, col: 3, type: 'pair', subtype: 'high-pair' },
  JTs: { hand: 'JTs', row: 3, col: 4, type: 'suited', subtype: 'broadway' },
  J9s: { hand: 'J9s', row: 3, col: 5, type: 'suited', subtype: 'suited-other' },
  J8s: { hand: 'J8s', row: 3, col: 6, type: 'suited', subtype: 'suited-other' },
  J7s: { hand: 'J7s', row: 3, col: 7, type: 'suited', subtype: 'suited-other' },
  J6s: { hand: 'J6s', row: 3, col: 8, type: 'suited', subtype: 'suited-other' },
  J5s: { hand: 'J5s', row: 3, col: 9, type: 'suited', subtype: 'suited-other' },
  J4s: { hand: 'J4s', row: 3, col: 10, type: 'suited', subtype: 'suited-other' },
  J3s: { hand: 'J3s', row: 3, col: 11, type: 'suited', subtype: 'suited-other' },
  J2s: { hand: 'J2s', row: 3, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 4 - Tens
  ATo: { hand: 'ATo', row: 4, col: 0, type: 'offsuit', subtype: 'broadway' },
  KTo: { hand: 'KTo', row: 4, col: 1, type: 'offsuit', subtype: 'broadway' },
  QTo: { hand: 'QTo', row: 4, col: 2, type: 'offsuit', subtype: 'broadway' },
  JTo: { hand: 'JTo', row: 4, col: 3, type: 'offsuit', subtype: 'broadway' },
  TT: { hand: 'TT', row: 4, col: 4, type: 'pair', subtype: 'high-pair' },
  T9s: { hand: 'T9s', row: 4, col: 5, type: 'suited', subtype: 'suited-connector' },
  T8s: { hand: 'T8s', row: 4, col: 6, type: 'suited', subtype: 'suited-other' },
  T7s: { hand: 'T7s', row: 4, col: 7, type: 'suited', subtype: 'suited-other' },
  T6s: { hand: 'T6s', row: 4, col: 8, type: 'suited', subtype: 'suited-other' },
  T5s: { hand: 'T5s', row: 4, col: 9, type: 'suited', subtype: 'suited-other' },
  T4s: { hand: 'T4s', row: 4, col: 10, type: 'suited', subtype: 'suited-other' },
  T3s: { hand: 'T3s', row: 4, col: 11, type: 'suited', subtype: 'suited-other' },
  T2s: { hand: 'T2s', row: 4, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 5 - Nines
  A9o: { hand: 'A9o', row: 5, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K9o: { hand: 'K9o', row: 5, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q9o: { hand: 'Q9o', row: 5, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J9o: { hand: 'J9o', row: 5, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T9o: { hand: 'T9o', row: 5, col: 4, type: 'offsuit', subtype: 'offsuit-connector' },
  '99': { hand: '99', row: 5, col: 5, type: 'pair', subtype: 'mid-pair' },
  '98s': { hand: '98s', row: 5, col: 6, type: 'suited', subtype: 'suited-connector' },
  '97s': { hand: '97s', row: 5, col: 7, type: 'suited', subtype: 'suited-one-gapper' },
  '96s': { hand: '96s', row: 5, col: 8, type: 'suited', subtype: 'suited-two-gapper' },
  '95s': { hand: '95s', row: 5, col: 9, type: 'suited', subtype: 'suited-other' },
  '94s': { hand: '94s', row: 5, col: 10, type: 'suited', subtype: 'suited-other' },
  '93s': { hand: '93s', row: 5, col: 11, type: 'suited', subtype: 'suited-other' },
  '92s': { hand: '92s', row: 5, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 6 - Eights
  A8o: { hand: 'A8o', row: 6, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K8o: { hand: 'K8o', row: 6, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q8o: { hand: 'Q8o', row: 6, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J8o: { hand: 'J8o', row: 6, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T8o: { hand: 'T8o', row: 6, col: 4, type: 'offsuit', subtype: 'offsuit-other' },
  '98o': { hand: '98o', row: 6, col: 5, type: 'offsuit', subtype: 'offsuit-connector' },
  '88': { hand: '88', row: 6, col: 6, type: 'pair', subtype: 'mid-pair' },
  '87s': { hand: '87s', row: 6, col: 7, type: 'suited', subtype: 'suited-connector' },
  '86s': { hand: '86s', row: 6, col: 8, type: 'suited', subtype: 'suited-one-gapper' },
  '85s': { hand: '85s', row: 6, col: 9, type: 'suited', subtype: 'suited-two-gapper' },
  '84s': { hand: '84s', row: 6, col: 10, type: 'suited', subtype: 'suited-other' },
  '83s': { hand: '83s', row: 6, col: 11, type: 'suited', subtype: 'suited-other' },
  '82s': { hand: '82s', row: 6, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 7 - Sevens
  A7o: { hand: 'A7o', row: 7, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K7o: { hand: 'K7o', row: 7, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q7o: { hand: 'Q7o', row: 7, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J7o: { hand: 'J7o', row: 7, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T7o: { hand: 'T7o', row: 7, col: 4, type: 'offsuit', subtype: 'offsuit-other' },
  '97o': { hand: '97o', row: 7, col: 5, type: 'offsuit', subtype: 'offsuit-one-gapper' },
  '87o': { hand: '87o', row: 7, col: 6, type: 'offsuit', subtype: 'offsuit-connector' },
  '77': { hand: '77', row: 7, col: 7, type: 'pair', subtype: 'mid-pair' },
  '76s': { hand: '76s', row: 7, col: 8, type: 'suited', subtype: 'suited-connector' },
  '75s': { hand: '75s', row: 7, col: 9, type: 'suited', subtype: 'suited-one-gapper' },
  '74s': { hand: '74s', row: 7, col: 10, type: 'suited', subtype: 'suited-two-gapper' },
  '73s': { hand: '73s', row: 7, col: 11, type: 'suited', subtype: 'suited-other' },
  '72s': { hand: '72s', row: 7, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 8 - Sixes
  A6o: { hand: 'A6o', row: 8, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K6o: { hand: 'K6o', row: 8, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q6o: { hand: 'Q6o', row: 8, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J6o: { hand: 'J6o', row: 8, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T6o: { hand: 'T6o', row: 8, col: 4, type: 'offsuit', subtype: 'offsuit-other' },
  '96o': { hand: '96o', row: 8, col: 5, type: 'offsuit', subtype: 'offsuit-other' },
  '86o': { hand: '86o', row: 8, col: 6, type: 'offsuit', subtype: 'offsuit-one-gapper' },
  '76o': { hand: '76o', row: 8, col: 7, type: 'offsuit', subtype: 'offsuit-connector' },
  '66': { hand: '66', row: 8, col: 8, type: 'pair', subtype: 'low-pair' },
  '65s': { hand: '65s', row: 8, col: 9, type: 'suited', subtype: 'suited-connector' },
  '64s': { hand: '64s', row: 8, col: 10, type: 'suited', subtype: 'suited-one-gapper' },
  '63s': { hand: '63s', row: 8, col: 11, type: 'suited', subtype: 'suited-two-gapper' },
  '62s': { hand: '62s', row: 8, col: 12, type: 'suited', subtype: 'suited-other' },

  // Row 9 - Fives
  A5o: { hand: 'A5o', row: 9, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K5o: { hand: 'K5o', row: 9, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q5o: { hand: 'Q5o', row: 9, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J5o: { hand: 'J5o', row: 9, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T5o: { hand: 'T5o', row: 9, col: 4, type: 'offsuit', subtype: 'offsuit-other' },
  '95o': { hand: '95o', row: 9, col: 5, type: 'offsuit', subtype: 'offsuit-other' },
  '85o': { hand: '85o', row: 9, col: 6, type: 'offsuit', subtype: 'offsuit-other' },
  '75o': { hand: '75o', row: 9, col: 7, type: 'offsuit', subtype: 'offsuit-one-gapper' },
  '65o': { hand: '65o', row: 9, col: 8, type: 'offsuit', subtype: 'offsuit-connector' },
  '55': { hand: '55', row: 9, col: 9, type: 'pair', subtype: 'low-pair' },
  '54s': { hand: '54s', row: 9, col: 10, type: 'suited', subtype: 'suited-connector' },
  '53s': { hand: '53s', row: 9, col: 11, type: 'suited', subtype: 'suited-one-gapper' },
  '52s': { hand: '52s', row: 9, col: 12, type: 'suited', subtype: 'suited-two-gapper' },

  // Row 10 - Fours
  A4o: { hand: 'A4o', row: 10, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K4o: { hand: 'K4o', row: 10, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q4o: { hand: 'Q4o', row: 10, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J4o: { hand: 'J4o', row: 10, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T4o: { hand: 'T4o', row: 10, col: 4, type: 'offsuit', subtype: 'offsuit-other' },
  '94o': { hand: '94o', row: 10, col: 5, type: 'offsuit', subtype: 'offsuit-other' },
  '84o': { hand: '84o', row: 10, col: 6, type: 'offsuit', subtype: 'offsuit-other' },
  '74o': { hand: '74o', row: 10, col: 7, type: 'offsuit', subtype: 'offsuit-other' },
  '64o': { hand: '64o', row: 10, col: 8, type: 'offsuit', subtype: 'offsuit-one-gapper' },
  '54o': { hand: '54o', row: 10, col: 9, type: 'offsuit', subtype: 'offsuit-connector' },
  '44': { hand: '44', row: 10, col: 10, type: 'pair', subtype: 'low-pair' },
  '43s': { hand: '43s', row: 10, col: 11, type: 'suited', subtype: 'suited-connector' },
  '42s': { hand: '42s', row: 10, col: 12, type: 'suited', subtype: 'suited-one-gapper' },

  // Row 11 - Threes
  A3o: { hand: 'A3o', row: 11, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K3o: { hand: 'K3o', row: 11, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q3o: { hand: 'Q3o', row: 11, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J3o: { hand: 'J3o', row: 11, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T3o: { hand: 'T3o', row: 11, col: 4, type: 'offsuit', subtype: 'offsuit-other' },
  '93o': { hand: '93o', row: 11, col: 5, type: 'offsuit', subtype: 'offsuit-other' },
  '83o': { hand: '83o', row: 11, col: 6, type: 'offsuit', subtype: 'offsuit-other' },
  '73o': { hand: '73o', row: 11, col: 7, type: 'offsuit', subtype: 'offsuit-other' },
  '63o': { hand: '63o', row: 11, col: 8, type: 'offsuit', subtype: 'offsuit-other' },
  '53o': { hand: '53o', row: 11, col: 9, type: 'offsuit', subtype: 'offsuit-one-gapper' },
  '43o': { hand: '43o', row: 11, col: 10, type: 'offsuit', subtype: 'offsuit-connector' },
  '33': { hand: '33', row: 11, col: 11, type: 'pair', subtype: 'low-pair' },
  '32s': { hand: '32s', row: 11, col: 12, type: 'suited', subtype: 'suited-connector' },

  // Row 12 - Twos
  A2o: { hand: 'A2o', row: 12, col: 0, type: 'offsuit', subtype: 'offsuit-ace' },
  K2o: { hand: 'K2o', row: 12, col: 1, type: 'offsuit', subtype: 'offsuit-other' },
  Q2o: { hand: 'Q2o', row: 12, col: 2, type: 'offsuit', subtype: 'offsuit-other' },
  J2o: { hand: 'J2o', row: 12, col: 3, type: 'offsuit', subtype: 'offsuit-other' },
  T2o: { hand: 'T2o', row: 12, col: 4, type: 'offsuit', subtype: 'offsuit-other' },
  '92o': { hand: '92o', row: 12, col: 5, type: 'offsuit', subtype: 'offsuit-other' },
  '82o': { hand: '82o', row: 12, col: 6, type: 'offsuit', subtype: 'offsuit-other' },
  '72o': { hand: '72o', row: 12, col: 7, type: 'offsuit', subtype: 'offsuit-other' },
  '62o': { hand: '62o', row: 12, col: 8, type: 'offsuit', subtype: 'offsuit-other' },
  '52o': { hand: '52o', row: 12, col: 9, type: 'offsuit', subtype: 'offsuit-other' },
  '42o': { hand: '42o', row: 12, col: 10, type: 'offsuit', subtype: 'offsuit-one-gapper' },
  '32o': { hand: '32o', row: 12, col: 11, type: 'offsuit', subtype: 'offsuit-connector' },
  '22': { hand: '22', row: 12, col: 12, type: 'pair', subtype: 'low-pair' },
};

/**
 * Array of all hand names in canonical order
 */
export const CANONICAL_HANDS = Object.keys(HAND_METADATA);

/**
 * Human-readable labels for hand types
 */
export const HAND_TYPE_LABELS: Record<HandType, string> = {
  pair: 'Pocket Pair',
  suited: 'Suited',
  offsuit: 'Offsuit',
};

/**
 * Human-readable labels for hand subtypes
 */
export const HAND_SUBTYPE_LABELS: Record<HandSubtype, string> = {
  'premium-pair': 'Premium Pair',
  'high-pair': 'High Pair',
  'mid-pair': 'Mid Pair',
  'low-pair': 'Low Pair',
  broadway: 'Broadway',
  'suited-connector': 'Suited Connector',
  'suited-one-gapper': 'Suited One-Gapper',
  'suited-two-gapper': 'Suited Two-Gapper',
  'suited-ace': 'Suited Ace',
  'suited-other': 'Suited',
  'offsuit-connector': 'Offsuit Connector',
  'offsuit-one-gapper': 'Offsuit One-Gapper',
  'offsuit-ace': 'Offsuit Ace',
  'offsuit-other': 'Offsuit',
};

/**
 * Get metadata for a hand by name
 */
export function getHandMetadata(hand: string): HandMetadata {
  const metadata = HAND_METADATA[hand];
  if (!metadata) {
    throw new Error(`Hand "${hand}" not found`);
  }
  return metadata;
}
